// @flow
import React from "react";
import PropTypes from 'prop-types';
import {reisy, use} from "reisy";
import Transition from "./transition.js";
import * as Items from "./items.js";
import User from "./user.js";
import Share, {ShareIcon} from "./share.js";
import * as Icons from "../components/icons.js";
import type Player from "../player/player.js";
import {hasPositionStickySupport, scrollbarSize} from "../environment.js";

export const styles = reisy`
@namespace Navigation;

JackInTheBox: $Settings.JackInTheBox;
NavBleed: $Settings.NavBleed;
UsePositionSticky: true;

ColorNav: rgba(0, 0, 0, 0.7); 
ColorNavLight: rgba(255, 255, 255, 0.9);
Foreground: $ColorNav;
Background: #FFF;
DefaultHeight: 56px;

Navigation {
	position: relative;
	z-index: 3;
	top: 0; right: 0; left: 0;
	height: 0;

	@media print {
		display: none;
	}

	&, & *, & *::after, & *::before {
		box-sizing: border-box;
	}
}

Fixed {
	position: fixed;
	backface-visibility: hidden;
}

Sticky {
	position: sticky;
}

WithBackground {
	background: $Background;

	&::after {
		content: "";
		display: block;
		position: absolute;
		bottom: 0;
		left: 0;
		transform: translateY(100%);
		height: 4px;
		width: 100%;
		background: linear-gradient(rgba(0, 0, 0, 0.10) 0%, rgba(0, 0, 0, 0) 100%);
	}
}

Limiter {
	$Kosk.Container & {
		width: 100%;
		max-width: $Settings.KoskMaxWidth;
		margin: 0 auto;
	}
}

Collapsed {
	z-index: 0;
	position: absolute;
	top: 0; right: 0; left: 0;
	transition: all $Consts.Transition;

	@extends WithBackground;
	&$Transparent {
		background: transparent;
		box-shadow: none;
		pointer-events: none;

		&::after {
			opacity: 0;
		}
	}
}
Expanded {
	z-index: 1;
	position: relative;
	transition: all $Consts.Transition;
	max-height: 100vh;
	overflow: scroll;
	padding-bottom: 100px;
	-webkit-overflow-scrolling: touch;
	&::after {
	  display: none;
	}

	&.enter {
		opacity: 0;
	}
	&.enter-active {
		opacity: 1;
	}
	&.leave-active {
		opacity: 0;
	}

	@extends WithBackground;
}

LimiterBleed {
	position: relative;

	$Kosk.Container &,
	$CollectionScreen.Container & {
		width: 100%;
		max-width: $Settings.KoskMaxWidth;
		margin: 0 auto;
	}
}

NavRow {
	display: flex;
	padding: 0 4px;
	flex-wrap: wrap;
}

/* Behaviors: */
OnTop {}
Transparent {
	background: transparent;
}
Static {
	position: relative;
	top: 0; left: 0; right: 0; bottom: 0;
	height: auto;
	z-index: auto;

	& $Expanded ~ $Collapsed {
		display: none;
	}

	& $Collapsed {
		position: relative;
		top: 0; left: 0; right: 0; bottom: 0;
	}

	& $Navigation {
		height: auto;
	}
}
Absolute {
	position: absolute;
	height: auto;

	& $Collapsed {
		position: absolute;
	}
}
DarkMode {
	@extends Transparent;
}
`;

type Layout = Array<Array<string>>;

function parseLayout(_layout: string) {
	const rows = _layout.toLowerCase().split("|");
	const layout = [];
	for (let i = 0; i < rows.length; i++) {
		let row = rows[i];
		row = row.split(/\s+/).filter(Boolean);
		if (row.length) {
			layout.push(row);
		}
	}
	return layout;
}

class Navigation extends React.Component {
	static contextTypes = {
		player: PropTypes.object,
	};
	context: {
		player: Player,
	};
	props: any;

	state: {
		expanded: boolean;
		stuck: boolean;
		onTop?: boolean;
	};

	elem: HTMLElement;
	setElem = (elem: HTMLElement) => (this.elem = elem);

	collapsedLayout: Layout;
	expandedLayout: Layout;
	behavior: Set<string>;

	constructor(props: any) {
		super(props);
		this.state = this.parseOptions(props);
		this.handleScroll = this.handleScroll.bind(this);
	}

	componentWillReceiveProps(props: any) {
		this.setState(this.parseOptions(props), () => {
			this.handleScroll();
		});
	}

	handleScroll = () => {
		if (!this.elem || this.state.isStatic || this.state.isAbsolute) { return; }

		if (styles.JackInTheBox) {
			this.setState({stuck: this.elem.getBoundingClientRect().top <= 0});
		}

		const offset = this.elem.offsetParent ? (this.elem.offsetParent: any).offsetTop : 0;
		const onTop = window.pageYOffset <= offset;

		if (onTop !== this.state.onTop) {
			this.setState({onTop});
		}
	}
	componentDidMount() {
		if (!IS_BROWSER) { return; }
		window.addEventListener("scroll", this.handleScroll);
		this.handleScroll();
	}
	componentWillUnmount() {
		window.removeEventListener("scroll", this.handleScroll);
	}
	parseOptions(props: any) {
		const {styles = {}, contrastHint} = props;

		let {
			navbarExpanded = "back - locale user share collapse",
			navbarCollapsed = "back - share", // replace this with expand \o/
			navbarBehavior = "transparent",
			navbarShow = true,
			showBack = true,
			showShare = true,
		} = styles;

		// Ye well, we need to have backwards compat to these props
		if (!showBack) {
			navbarCollapsed = navbarCollapsed.replace("back", "");
		}
		if (!showShare) {
			navbarCollapsed = navbarCollapsed.replace("share", "");
		}
		navbarCollapsed = navbarCollapsed.replace("none", "");

		let darkMode = false;
		if (contrastHint) {
			const color = contrastHint.startsWith('#') ? contrastHint.slice(1).match(/[a-f0-9]{2}/g).map(c => parseInt(c, 16)) : contrastHint.replace(/rgba?\(/, '').replace(')', '').split(',').map(parseFloat);
			const alpha = color[3] || 1;
			const lum = ((1 - alpha) * 255 + alpha * (0.2126 * color[0] + 0.7152 * color[1] + 0.0722 * color[2])) / 255;
			darkMode = lum < .5;
		}

		this.collapsedLayout = parseLayout(navbarCollapsed);
		this.expandedLayout = parseLayout(navbarExpanded);
		this.behavior = new Set(navbarBehavior.split(/\s+/));
		return {
			expanded: this.behavior.has("expanded"),
			isStatic: this.behavior.has("static"),
			isAbsolute: this.behavior.has("absolute"),
			onTop: true,
			show: navbarShow,
			darkMode,
		};
	}

	handleExpand = () => this.setState({expanded: true});
	handleCollapse = () => this.setState({expanded: false});

	renderItem(item: string): any {
		const {player} = this.context;
		const locale = player && player.options ? player.options.locale : null;
		const Branding = (this.props.styles && this.props.styles.Branding) || false;
		switch (item) {
		case "back":
			return player.back && <Items.Back href={player.back} />;
		case "back-share-base":
			return <Items.Back href={player.options.shareBaseUrl} />;
		case "back-close":
			return player.back && <Items.Back icon={Icons.CloseCross} href={player.back} />;
		case "back-arrow":
			return player.back && <Items.Back icon={Icons.BackArrow} href={player.back} />;
		case "share":
			return player.content && <Share content={(player.content: any)} />;
		case "share-left":
			return player.content && <Share content={(player.content: any)} side="left" />;
		case "share-right":
			return player.content && <Share content={(player.content: any)} side="right" />;
		case "like":
			return player.content && <Items.Like content={(player.content: any)} />;
		case "expand":
			return <Items.Button item="expand" onClick={this.handleExpand} title={"de" === locale ? "Menü öffnen" : "Open Menu"}><Icons.Expand /></Items.Button>;
		case "collapse":
			return <Items.Button item="collapse" onClick={this.handleCollapse} title={"de" === locale ? "Menü schließen" : "Close Menu"}><Icons.Close /></Items.Button>;
		case "user":
			return <User user={player.user} />;
		case "publishertitle":
			return <Items.PublisherTitle publisher={player.publisher} />;
		case "publishername":
			return <Items.PublisherName publisher={player.publisher} />;
		case "publisherslogan":
			return <Items.PublisherSlogan publisher={player.publisher} />;
		case "publisherdescription":
			return <Items.PublisherDescription publisher={player.publisher} />;
		case "publisherlogo":
			return <Items.PublisherLogo publisher={player.publisher} />;
		case "pagestriplogo":
			return Branding ? <Items.PagestripLogo /> : null;
		case "pagestrip":
			// Easiest way to disable branding?
			return Branding ? <Items.Pagestrip /> : <Items.PublisherLogo publisher={player} />;
		case "search":
			return <Items.Search />;
		case "searchtoggle":
			return <Items.SearchToggle />;
		case "menu":
			return <Items.Menu player={player} onItemClick={this.handleCollapse} />;
		case "tags":
			return <Items.Tags player={player} />;
		case "zoom100":
			return <Items.Zoom player={player} zoom={1} />;
		case "zoom150":
			return <Items.Zoom player={player} zoom={1.5} />;
		case "zoom200":
			return <Items.Zoom player={player} zoom={2} />;
		case "locale":
			return <Items.Locale player={player} translations={this.props.translations} />;
		case "localecompact":
			return <Items.LocaleCompact player={player} translations={this.props.translations} />;
		case "pdf":
			return <Items.ShowPDF player={player} />;
		case "taglist":
			return <Items.TagList publishable={player.content} />;
		case "logout":
			return <Items.Logout player={player} />;
		case "-":
		default:
			if (item.startsWith('menu-publishableclass')) {
				const menuID = player.content.options && player.content.options.className && parseInt(player.content.options.className.split('-')[1]);

				return menuID ? <Items.Menu player={player} menuid={menuID} /> : null;
			}
			if (item.startsWith('menu-')) {
				return <Items.Menu player={player} menuid={parseInt(item.split('-')[1])} />;
			}
			if (item.startsWith('share-')) {
				return player.content && <ShareIcon destination={item.replace('share-', '')} content={player.content} />;
			}
			if (item.startsWith('tagselect(')) {
				const menuIds = item.match(/tagselect\((.*?)\)/)[1].split(',').map(v => parseInt(v, 10)) 
				return <Items.TagSelect player={player} menuIds={menuIds} />
			}
			if (item.startsWith('taglist(/')) {
				const rex = new RegExp(item.match(/taglist\(\/(.*?)\/\)/)[1]);
				return <Items.TagList publishable={player.content} filter={i => i.name.match(rex)} />
			}
			return <Items.Spacer />;
		}
	}

	_renderItem = (_item: string, i: number) => {
		const item = this.renderItem(_item);
		if (item) {
			return React.cloneElement(item, {key: i});
		}
		return null;
	}

	render() {
		const {expanded, onTop, stuck, isStatic, isAbsolute, show, darkMode} = this.state;
		const {collapsedLayout, behavior} = this;
		const uniqueClass = this.props.styles && this.props.styles.Navigation && this.props.styles.Navigation;
		const {player} = this.context;

		if ((!expanded && !collapsedLayout.length) || !show) {
			return null;
		}

		const isTransparent = behavior.has("transparent") || (behavior.has("transparentontop") && onTop);

		const usePositionSticky = styles.UsePositionSticky && hasPositionStickySupport;

		return (
			<nav ref={this.setElem} {...use(
					// sticky must be placed on outer element
					styles.JackInTheBox
						? ([styles.Navigation, usePositionSticky && styles.Sticky])
						: ([styles.Fixed, styles.Navigation]),
					onTop && styles.OnTop,
					isStatic && styles.Static,
					isAbsolute && styles.Absolute,
					uniqueClass && uniqueClass,
				)}
				lang={player && player.options && player.options.locale}>

				<div {...use(
					// fixed must be placed on inner element to use outer as reference to top
					(styles.JackInTheBox && !usePositionSticky) &&
						([
							styles.Navigation,
							stuck && ([styles.Fixed, {marginRight: `${scrollbarSize}px`}])])
					)}>

					<Transition
						element="div" {...use(styles.Expanded)} render={expanded}>
						<div>
							{this.expandedLayout.map((row, i) => <div key={i} {...use(styles.NavRow)}>
								{row.map(this._renderItem)}
							</div>)}
						</div>
					</Transition>
					<div
						{...use(
							isTransparent ? darkMode ? styles.DarkMode : styles.Transparent : null,
							styles.Collapsed, !styles.NavBleed && styles.Limiter)}>
						<div {...use(styles.NavBleed && styles.LimiterBleed)}>
							{collapsedLayout.map((row, i) => <div key={i} {...use(styles.NavRow)}>
								{row.map(this._renderItem)}
							</div>)}
						</div>
					</div>

				</div>
			</nav>
		);
	}
}

export default Navigation;



// WEBPACK FOOTER //
// ./lib/navigation/index.js